import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
    ArrowLeft, Shield, ExternalLink, Copy, Send, MessageSquare,
    Phone, Mail, Building2, CheckCircle2, Circle, ChevronRight,
    Loader2, Clock, Zap, Pencil, Check, X,
    // Dossier icons
    FileText, Key, HardDrive, Bot, BookOpen, StickyNote,
    Download, Plus, Trash2, Eye, EyeOff, RefreshCw,
    Upload, Link2, Table, AlertTriangle, Monitor, Users, Save,
    CheckCircle,
} from 'lucide-react';

// ─── Error Boundary (catches crashes in lazy-loaded modals) ──────────────────
class ModalErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { error: null }; }
    static getDerivedStateFromError(error) { return { error }; }
    componentDidCatch(error, info) { console.error('[ModalErrorBoundary]', error, info); }
    render() {
        if (this.state.error) {
            return (
                <div className="fixed inset-0 z-[200] bg-[#0A0A14] flex flex-col items-center justify-center gap-4 p-8">
                    <AlertTriangle className="w-10 h-10 text-red-400" />
                    <p className="text-red-400 font-bold">Editor failed to load</p>
                    <p className="text-sm text-[#F0EFF4]/40 text-center max-w-md">{this.state.error.message}</p>
                    <button
                        onClick={() => { this.setState({ error: null }); this.props.onClose?.(); }}
                        className="px-4 py-2 bg-[#7B61FF] text-white text-sm font-bold rounded-lg"
                    >
                        Close
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

const MarkdownEditorModal = React.lazy(() => import('../../components/admin/MarkdownEditorModal'));
import ClientNotes from '../../components/admin/ClientNotes';

// ─── Constants ────────────────────────────────────────────────────────────────

const STAGE_ORDER = [
    'Committed',
    'Pre-consult Scheduled',
    'Pre-consult Complete',
    'Building',
    'Delivered',
    'Ongoing Support',
];

const STAGE_COLORS = {
    'Committed':             'bg-[#7B61FF]/20 text-[#7B61FF] ring-[#7B61FF]/30',
    'Pre-consult Scheduled': 'bg-blue-500/20 text-blue-400 ring-blue-500/30',
    'Pre-consult Complete':  'bg-teal-500/20 text-teal-400 ring-teal-500/30',
    'Building':              'bg-orange-500/20 text-orange-400 ring-orange-500/30',
    'Delivered':             'bg-green-500/20 text-green-400 ring-green-500/30',
    'Ongoing Support':       'bg-emerald-500/20 text-emerald-400 ring-emerald-500/30',
};

const UPDATE_ICONS = { milestone: '🏆', link: '👀', shipped: '📦', info: 'ℹ️' };

const CONFIG_FILES = [
    { name: 'SOUL.md',      fieldName: 'soul_md' },
    { name: 'IDENTITY.md',  fieldName: 'identity_md' },
    { name: 'AGENTS.md',    fieldName: 'agents_md' },
    { name: 'HEARTBEAT.md', fieldName: 'heartbeat_md' },
    { name: 'USER.md',      fieldName: 'user_md' },
    { name: 'TOOLS.md',     fieldName: 'tools_md' },
];

const DOC_STATUS_STYLES = {
    received:          'bg-[#F0EFF4]/10 text-[#F0EFF4]/60',
    filed:             'bg-blue-500/20 text-blue-400',
    reviewed:          'bg-emerald-500/20 text-emerald-400',
    included_in_build: 'bg-[#7B61FF]/20 text-[#7B61FF]',
};

const AGENT_STATUS_STYLES = {
    planned:    'bg-[#F0EFF4]/10 text-[#F0EFF4]/50',
    configured: 'bg-yellow-500/20 text-yellow-400',
    live:       'bg-emerald-500/20 text-emerald-400',
};

const HW_STATUS_STYLES = {
    available: 'bg-emerald-500/20 text-emerald-400',
    assigned:  'bg-yellow-500/20 text-yellow-400',
    deployed:  'bg-blue-500/20 text-blue-400',
};

// ─── Stage Dropdown (shared with AdminDashboard pattern) ─────────────────
const StageDropdown = ({ stage, onSelect, colorClass }) => {
    const [open, setOpen] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleSelect = async (s) => {
        if (s === stage) { setOpen(false); return; }
        setSaving(true);
        try { await onSelect(s); } finally { setSaving(false); setOpen(false); }
    };

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(v => !v)}
                disabled={saving}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-data font-semibold ring-1 transition-all cursor-pointer hover:scale-105 ${saving ? 'opacity-50' : ''} ${colorClass}`}
            >
                {saving ? '...' : stage}
                <svg className="w-3.5 h-3.5 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {open && (
                <div className="absolute z-50 mt-1 right-0 min-w-[200px] bg-[#18181B] border border-[#F0EFF4]/10 rounded-lg shadow-xl py-1">
                    {STAGE_ORDER.map(s => {
                        const sc = STAGE_COLORS[s] || '';
                        const isCurrent = s === stage;
                        return (
                            <button
                                key={s}
                                onClick={() => handleSelect(s)}
                                className={`w-full text-left px-3 py-2 text-sm font-data transition-colors flex items-center gap-2
                                    ${isCurrent ? 'bg-[#F0EFF4]/10 font-bold' : 'hover:bg-[#F0EFF4]/5'}`}
                            >
                                <span className={`w-2 h-2 rounded-full ${sc.split(' ')[0] || 'bg-white/20'}`} />
                                {s}
                                {isCurrent && <span className="text-[#7B61FF] ml-auto">✓</span>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const daysAgo = (dateStr) => {
    if (!dateStr) return 0;
    return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000);
};

// ─── Shared sub-components ────────────────────────────────────────────────────

const Section = ({ title, icon: Icon, children, action }) => (
    <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
                {Icon && <Icon className="w-4 h-4 text-[#7B61FF]" />}
                <h3 className="text-sm font-bold text-[#F0EFF4] uppercase tracking-wider font-data">{title}</h3>
            </div>
            {action}
        </div>
        {children}
    </div>
);

const MaskedValue = ({ value, id, revealed, onToggle }) => {
    if (!value) return <span className="text-[#F0EFF4]/20 italic text-xs">—</span>;
    return (
        <span className="font-data text-xs flex items-center gap-1">
            {revealed ? value : '••••••••'}
            <button onClick={() => onToggle(id)} className="text-[#F0EFF4]/30 hover:text-[#7B61FF] transition-colors ml-1">
                {revealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            </button>
        </span>
    );
};

const CopyBtn = ({ value }) => {
    const [copied, setCopied] = useState(false);
    if (!value) return null;
    const handleCopy = () => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    return (
        <button onClick={handleCopy} className="text-[#F0EFF4]/30 hover:text-[#7B61FF] transition-colors">
            {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
        </button>
    );
};

const EditableField = ({ label, value, fieldKey, clientId, password, getAuthHeaders, onUpdate }) => {
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState(value || '');
    const [saving, setSaving] = useState(false);
    const isLong = (value || '').length > 60;

    const save = async () => {
        if (editValue === (value || '')) { setEditing(false); return; }
        setSaving(true);
        try {
            const headers = getAuthHeaders
                ? getAuthHeaders()
                : { 'Content-Type': 'application/json', 'x-admin-password': password };
            const res = await fetch('/api/update-intake', {
                method: 'PUT',
                headers,
                body: JSON.stringify({ client_id: clientId, updates: { [fieldKey]: editValue } }),
            });
            if (!res.ok) throw new Error('Failed to save');
            onUpdate(fieldKey, editValue);
            setEditing(false);
        } catch (err) { alert(err.message); }
        finally { setSaving(false); }
    };

    if (editing) {
        return (
            <div className="bg-[#0A0A14]/50 rounded-lg p-3">
                <div className="text-[#F0EFF4]/40 text-xs mb-1">{label}</div>
                {isLong ? (
                    <textarea
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        className="w-full bg-[#18181B] border border-[#7B61FF] rounded-lg px-3 py-2 text-sm text-[#F0EFF4] focus:outline-none resize-y min-h-[80px]"
                        autoFocus
                    />
                ) : (
                    <input
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        className="w-full bg-[#18181B] border border-[#7B61FF] rounded-lg px-3 py-2 text-sm text-[#F0EFF4] focus:outline-none"
                        autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
                    />
                )}
                <div className="flex gap-2 mt-2 justify-end">
                    <button onClick={() => setEditing(false)} className="p-1 text-[#F0EFF4]/40 hover:text-[#F0EFF4]"><X className="w-4 h-4" /></button>
                    <button onClick={save} disabled={saving} className="p-1 text-[#7B61FF] hover:text-[#6B51EF]">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#0A0A14]/50 rounded-lg p-3 group relative">
            <div className="text-[#F0EFF4]/40 text-xs mb-1">{label}</div>
            <div className="text-[#F0EFF4] font-semibold text-sm pr-6 break-all">{value || <span className="text-[#F0EFF4]/20 italic font-normal">Not set</span>}</div>
            <button
                onClick={() => { setEditValue(value || ''); setEditing(true); }}
                className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 text-[#F0EFF4]/30 hover:text-[#7B61FF] transition-all"
                title={`Edit ${label}`}
            >
                <Pencil className="w-3.5 h-3.5" />
            </button>
        </div>
    );
};

// ─── Email Segment Badge ──────────────────────────────────────────────────────

const SEGMENT_STYLES = {
    'A':       'bg-red-500/20 text-red-400 ring-red-500/30',
    'A+':      'bg-orange-500/20 text-orange-400 ring-orange-500/30',
    'B_early': 'bg-yellow-500/20 text-yellow-400 ring-yellow-500/30',
    'B':       'bg-blue-500/20 text-blue-400 ring-blue-500/30',
    'C':       'bg-teal-500/20 text-teal-400 ring-teal-500/30',
    'D':       'bg-emerald-500/20 text-emerald-400 ring-emerald-500/30',
};

const SEGMENT_LABELS = {
    'A':       'Chase for intake',
    'A+':      'Intake done, queued',
    'B_early': 'Build started',
    'B':       'Build in progress',
    'C':       'Ready for delivery',
    'D':       'Delivered',
};

const EmailSegmentBadge = ({ segment, suppressUntil, lastVitoContact, lastAutoEmail, clientId, password, onRefresh }) => {
    const [showSuppress, setShowSuppress] = useState(false);
    const [suppressDate, setSuppressDate] = useState('');
    const [saving, setSaving] = useState(false);

    const isSuppressed = suppressUntil && new Date(suppressUntil) > new Date();
    const segmentStyle = SEGMENT_STYLES[segment] || 'bg-[#F0EFF4]/10 text-[#F0EFF4]/50';

    const handleSetSuppress = async () => {
        if (!suppressDate) return;
        setSaving(true);
        try {
            const res = await fetch('/api/update-client-status', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ client_id: clientId, suppress_email_until: new Date(suppressDate).toISOString() }),
            });
            if (!res.ok) throw new Error('Failed to set suppress');
            setShowSuppress(false);
            setSuppressDate('');
            if (onRefresh) onRefresh();
        } catch (err) { alert(err.message); }
        finally { setSaving(false); }
    };

    const handleClearSuppress = async () => {
        setSaving(true);
        try {
            const res = await fetch('/api/update-client-status', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ client_id: clientId, suppress_email_until: null }),
            });
            if (!res.ok) throw new Error('Failed to clear suppress');
            if (onRefresh) onRefresh();
        } catch (err) { alert(err.message); }
        finally { setSaving(false); }
    };

    return (
        <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-[#7B61FF]" />
                <h3 className="text-sm font-bold text-[#F0EFF4] uppercase tracking-wider font-data">Email Status</h3>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                {/* Segment badge */}
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-data font-bold ring-1 ${segmentStyle}`}>
                    Segment {segment || '—'}
                </span>
                <span className="text-xs text-[#F0EFF4]/40">{SEGMENT_LABELS[segment] || ''}</span>

                {/* Suppress indicator */}
                {isSuppressed && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-data font-semibold bg-yellow-500/20 text-yellow-400 ring-1 ring-yellow-500/30">
                        🔇 Suppressed until {new Date(suppressUntil).toLocaleDateString()}
                        <button onClick={handleClearSuppress} disabled={saving} className="ml-1 hover:text-white transition-colors">✕</button>
                    </span>
                )}
            </div>

            {/* Contact timestamps */}
            <div className="flex flex-wrap gap-4 mt-3 text-xs text-[#F0EFF4]/40 font-data">
                <span>
                    Vito last contact: {lastVitoContact
                        ? `${daysAgo(lastVitoContact)}d ago`
                        : <span className="text-red-400">never</span>
                    }
                </span>
                <span>
                    Last auto-email: {lastAutoEmail
                        ? `${daysAgo(lastAutoEmail)}d ago`
                        : 'never'
                    }
                </span>
            </div>

            {/* Suppress control */}
            <div className="mt-3">
                {!showSuppress ? (
                    <button
                        onClick={() => setShowSuppress(true)}
                        className="text-xs text-[#F0EFF4]/30 hover:text-[#F0EFF4]/60 transition-colors"
                    >
                        {isSuppressed ? 'Change suppress date' : '+ Suppress emails'}
                    </button>
                ) : (
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            value={suppressDate}
                            onChange={e => setSuppressDate(e.target.value)}
                            className="bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-lg px-2 py-1 text-xs text-[#F0EFF4] focus:border-[#7B61FF] focus:outline-none"
                        />
                        <button
                            onClick={handleSetSuppress}
                            disabled={saving || !suppressDate}
                            className="px-3 py-1 bg-[#7B61FF] text-white text-xs font-bold rounded-lg disabled:opacity-40"
                        >
                            {saving ? '...' : 'Set'}
                        </button>
                        <button onClick={() => setShowSuppress(false)} className="text-xs text-[#F0EFF4]/30 hover:text-[#F0EFF4]">Cancel</button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Build Status Banner ──────────────────────────────────────────────────────

const BuildStatusBanner = ({ client }) => {
    const p = client.progress || {};
    const bp = client.build_package;
    const allPrereqs = p.intake_complete && p.apple_id_ready && p.tailscale_ready &&
                       p.agent_email_ready && p.credentials_submitted;
    const configsGenerated = bp && (bp.config_generated_at || bp.soul_md);

    let color, text, icon;

    if (bp?.status === 'deployed') {
        color = 'bg-blue-500/20 border-blue-500/30 text-blue-300';
        text = `DEPLOYED ✓ — ${bp.deployed_at ? new Date(bp.deployed_at).toLocaleDateString() : 'date unknown'}`;
        icon = <CheckCircle className="w-4 h-4 shrink-0" />;
    } else if (bp?.status === 'ready') {
        color = 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300';
        text = 'READY TO BUILD';
        icon = <CheckCircle2 className="w-4 h-4 shrink-0" />;
    } else if (allPrereqs && configsGenerated) {
        color = 'bg-yellow-500/20 border-yellow-500/30 text-yellow-300';
        text = 'DRAFT — Review config files and mark ready';
        icon = <AlertTriangle className="w-4 h-4 shrink-0" />;
    } else {
        const missing = [];
        if (!p.intake_complete)       missing.push('Intake');
        if (!p.apple_id_ready)        missing.push('Apple ID');
        if (!p.tailscale_ready)       missing.push('Tailscale');
        if (!p.agent_email_ready)     missing.push('Email');
        if (!p.credentials_submitted) missing.push('Creds');
        if (!configsGenerated)        missing.push('Config Files');
        color = 'bg-red-500/20 border-red-500/30 text-red-300';
        text = `BLOCKED — missing: ${missing.join(', ') || 'unknown'}`;
        icon = <AlertTriangle className="w-4 h-4 shrink-0" />;
    }

    return (
        <div className={`flex items-center gap-3 px-5 py-3 rounded-xl border font-data text-sm font-semibold tracking-wide ${color}`}>
            {icon}
            <span>{text}</span>
        </div>
    );
};

// ─── Config Files Section ─────────────────────────────────────────────────────

const ConfigFilesSection = ({ client, password, onRefresh, onEditFile }) => {
    const [generating, setGenerating] = useState(false);
    const bp = client.build_package;
    const hasConfigs = bp && (bp.config_generated_at || bp.soul_md);

    const handleGenerate = async (regenerate = false) => {
        if (regenerate && !window.confirm('Regenerate all config files? This will overwrite existing content.')) return;
        setGenerating(true);
        try {
            const res = await fetch('/api/generate-config-files', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ client_id: client.id }),
            });
            if (!res.ok) throw new Error('Failed to generate config files');
            await onRefresh();
        } catch (err) {
            alert(err.message);
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Section
            title="Config Files"
            icon={FileText}
            action={
                hasConfigs ? (
                    <button
                        onClick={() => handleGenerate(true)}
                        disabled={generating}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F0EFF4]/5 hover:bg-[#F0EFF4]/10 border border-[#F0EFF4]/10 rounded-lg text-xs text-[#F0EFF4]/60 hover:text-[#F0EFF4] transition-all font-semibold disabled:opacity-40"
                    >
                        {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        Regenerate All
                    </button>
                ) : null
            }
        >
            {!hasConfigs ? (
                <div className="flex flex-col items-center gap-4 py-6">
                    <p className="text-sm text-[#F0EFF4]/40 italic">Config files not yet generated.</p>
                    <button
                        onClick={() => handleGenerate(false)}
                        disabled={generating}
                        className="flex items-center gap-2 px-5 py-2.5 bg-[#7B61FF] hover:bg-[#6B51EF] text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-40"
                    >
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        Generate Config Files
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {CONFIG_FILES.map((cf) => {
                        const content = bp?.[cf.fieldName] || '';
                        const charCount = content.length;
                        return (
                            <div key={cf.fieldName} className="bg-[#0A0A14]/60 border border-[#F0EFF4]/10 rounded-xl p-4 flex flex-col gap-2 group">
                                <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-[#7B61FF] shrink-0" />
                                    <span className="font-data text-xs font-bold text-[#F0EFF4] truncate">{cf.name}</span>
                                </div>
                                {charCount > 0 ? (
                                    <span className="text-xs text-emerald-400 font-data">{charCount.toLocaleString()} chars</span>
                                ) : (
                                    <span className="text-xs text-[#F0EFF4]/20 italic">Not generated</span>
                                )}
                                <div className="text-xs text-[#F0EFF4]/30">
                                    {bp?.updated_at ? new Date(bp.updated_at).toLocaleDateString() : '—'}
                                </div>
                                <button
                                    onClick={() => onEditFile({ ...cf, content })}
                                    className="mt-auto flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#7B61FF]/10 hover:bg-[#7B61FF]/20 border border-[#7B61FF]/20 rounded-lg text-xs text-[#7B61FF] font-semibold transition-colors"
                                >
                                    <Pencil className="w-3 h-3" /> Edit
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </Section>
    );
};

// ─── Credentials Section ──────────────────────────────────────────────────────

const CredentialsSection = ({ client, password, onRefresh }) => {
    const creds = client.credential_values || [];
    const submissions = client.credential_submissions || [];
    const pendingSubs = submissions.filter(s => !s.retrieved);
    const [showAdd, setShowAdd] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [revealed, setRevealed] = useState({});
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [markingRetrieved, setMarkingRetrieved] = useState(null);
    const [form, setForm] = useState({ integration_name: '', username: '', password: '', api_key: '', notes: '' });
    const [editForm, setEditForm] = useState({});

    const handleMarkRetrieved = async (subId) => {
        setMarkingRetrieved(subId);
        try {
            const res = await fetch('/api/mark-credential-retrieved', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ submission_id: subId }),
            });
            if (!res.ok) throw new Error('Failed to mark as retrieved');
            await onRefresh();
        } catch (err) {
            alert(err.message);
        } finally {
            setMarkingRetrieved(null);
        }
    };

    const toggleReveal = (key) => setRevealed(r => ({ ...r, [key]: !r[key] }));

    const handleAdd = async () => {
        if (!form.integration_name) return alert('Integration name required');
        setSaving(true);
        try {
            const res = await fetch('/api/save-credential-value', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ client_id: client.id, ...form }),
            });
            if (!res.ok) throw new Error('Failed to save credential');
            setForm({ integration_name: '', username: '', password: '', api_key: '', notes: '' });
            setShowAdd(false);
            await onRefresh();
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = async (id) => {
        setSaving(true);
        try {
            const res = await fetch('/api/save-credential-value', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ id, client_id: client.id, ...editForm }),
            });
            if (!res.ok) throw new Error('Failed to update credential');
            setEditingId(null);
            await onRefresh();
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this credential?')) return;
        setDeleting(id);
        try {
            const res = await fetch('/api/save-credential-value', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ id, client_id: client.id }),
            });
            if (!res.ok) throw new Error('Failed to delete');
            await onRefresh();
        } catch (err) {
            alert(err.message);
        } finally {
            setDeleting(null);
        }
    };

    const inputCls = 'bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-lg px-2 py-1.5 text-xs text-[#F0EFF4] placeholder:text-[#F0EFF4]/20 focus:border-[#7B61FF] focus:outline-none transition-colors w-full font-data';

    return (
        <Section
            title="Captured Credentials"
            icon={Key}
            action={
                <button
                    onClick={() => setShowAdd(v => !v)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7B61FF]/10 hover:bg-[#7B61FF]/20 border border-[#7B61FF]/20 rounded-lg text-xs text-[#7B61FF] font-semibold transition-colors"
                >
                    <Plus className="w-3.5 h-3.5" /> Add Credential
                </button>
            }
        >
            {/* Pending PW Pusher Submissions */}
            {pendingSubs.length > 0 && (
                <div className="mb-4 p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-yellow-400" />
                        <span className="text-sm font-bold text-yellow-400">
                            {pendingSubs.length} Pending Credential{pendingSubs.length > 1 ? 's' : ''} — Open & Save
                        </span>
                    </div>
                    <p className="text-xs text-[#F0EFF4]/40 mb-3">
                        Client submitted PW Pusher links. Open each link, copy the credential, then add it above and mark as retrieved.
                    </p>
                    <div className="space-y-2">
                        {pendingSubs.map(sub => (
                            <div key={sub.id} className="flex items-center justify-between gap-3 p-2.5 bg-[#0A0A14]/60 rounded-lg border border-[#F0EFF4]/5">
                                <div className="flex-1 min-w-0">
                                    <span className="text-xs font-semibold text-[#F0EFF4]">{sub.integration_name || 'Unknown'}</span>
                                    <div className="text-xs text-[#F0EFF4]/30 mt-0.5">
                                        Submitted {new Date(sub.submitted_at).toLocaleDateString()} via {sub.method || 'pwpusher'}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {sub.pwpush_url && (
                                        <a
                                            href={sub.pwpush_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-1 px-2.5 py-1.5 bg-[#7B61FF]/10 hover:bg-[#7B61FF]/20 border border-[#7B61FF]/20 rounded-lg text-xs text-[#7B61FF] font-semibold transition-colors"
                                        >
                                            <ExternalLink className="w-3 h-3" /> Open Link
                                        </a>
                                    )}
                                    <button
                                        onClick={() => handleMarkRetrieved(sub.id)}
                                        disabled={markingRetrieved === sub.id}
                                        className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 rounded-lg text-xs text-emerald-400 font-semibold transition-colors disabled:opacity-50"
                                    >
                                        {markingRetrieved === sub.id
                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                            : <CheckCircle className="w-3 h-3" />}
                                        Retrieved
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {creds.length === 0 && !showAdd && pendingSubs.length === 0 ? (
                <p className="text-sm text-[#F0EFF4]/30 italic py-2">
                    No credentials captured yet. Client hasn't submitted any yet.
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-[#F0EFF4]/10 text-[#F0EFF4]/40 font-data uppercase tracking-wider">
                                <th className="text-left pb-2 pr-3">Integration</th>
                                <th className="text-left pb-2 pr-3">Username</th>
                                <th className="text-left pb-2 pr-3">Password</th>
                                <th className="text-left pb-2 pr-3">API Key</th>
                                <th className="text-right pb-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F0EFF4]/5">
                            {creds.map((cred) => (
                                editingId === cred.id ? (
                                    <tr key={cred.id} className="py-2">
                                        <td className="py-2 pr-2"><input className={inputCls} value={editForm.integration_name} onChange={e => setEditForm(f => ({ ...f, integration_name: e.target.value }))} /></td>
                                        <td className="py-2 pr-2"><input className={inputCls} value={editForm.username} onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))} /></td>
                                        <td className="py-2 pr-2"><input className={inputCls} type="password" value={editForm.password} onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))} /></td>
                                        <td className="py-2 pr-2"><input className={inputCls} type="password" value={editForm.api_key} onChange={e => setEditForm(f => ({ ...f, api_key: e.target.value }))} /></td>
                                        <td className="py-2 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => handleEdit(cred.id)} disabled={saving} className="p-1 text-emerald-400 hover:text-emerald-300">
                                                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                                </button>
                                                <button onClick={() => setEditingId(null)} className="p-1 text-[#F0EFF4]/40 hover:text-[#F0EFF4]"><X className="w-3.5 h-3.5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={cred.id} className="group">
                                        <td className="py-2 pr-3 font-semibold text-[#F0EFF4]">{cred.integration_name}</td>
                                        <td className="py-2 pr-3 text-[#F0EFF4]/70 font-data">{cred.username || '—'}</td>
                                        <td className="py-2 pr-3">
                                            <div className="flex items-center gap-1">
                                                <MaskedValue value={cred.password} id={`pw-${cred.id}`} revealed={revealed[`pw-${cred.id}`]} onToggle={toggleReveal} />
                                                <CopyBtn value={cred.password} />
                                            </div>
                                        </td>
                                        <td className="py-2 pr-3">
                                            <div className="flex items-center gap-1">
                                                <MaskedValue value={cred.api_key} id={`ak-${cred.id}`} revealed={revealed[`ak-${cred.id}`]} onToggle={toggleReveal} />
                                                <CopyBtn value={cred.api_key} />
                                            </div>
                                        </td>
                                        <td className="py-2 text-right">
                                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => { setEditingId(cred.id); setEditForm({ integration_name: cred.integration_name, username: cred.username || '', password: '', api_key: '', notes: cred.notes || '' }); }}
                                                    className="p-1 text-[#F0EFF4]/40 hover:text-[#7B61FF]"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(cred.id)}
                                                    disabled={deleting === cred.id}
                                                    className="p-1 text-[#F0EFF4]/40 hover:text-red-400"
                                                >
                                                    {deleting === cred.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add credential form */}
            {showAdd && (
                <div className="mt-4 p-4 bg-[#0A0A14]/60 rounded-xl border border-[#7B61FF]/20 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs text-[#F0EFF4]/40 mb-1 block">Integration Name *</label>
                            <input className={inputCls} placeholder="e.g. JobNimbus" value={form.integration_name} onChange={e => setForm(f => ({ ...f, integration_name: e.target.value }))} />
                        </div>
                        <div>
                            <label className="text-xs text-[#F0EFF4]/40 mb-1 block">Username / Email</label>
                            <input className={inputCls} placeholder="user@example.com" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
                        </div>
                        <div>
                            <label className="text-xs text-[#F0EFF4]/40 mb-1 block">Password</label>
                            <input className={inputCls} type="password" placeholder="••••••••" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                        </div>
                        <div>
                            <label className="text-xs text-[#F0EFF4]/40 mb-1 block">API Key</label>
                            <input className={inputCls} type="password" placeholder="••••••••" value={form.api_key} onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))} />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-[#F0EFF4]/40 mb-1 block">Notes</label>
                        <input className={inputCls} placeholder="Optional notes..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs text-[#F0EFF4]/40 hover:text-[#F0EFF4] transition-colors">Cancel</button>
                        <button onClick={handleAdd} disabled={saving} className="flex items-center gap-1.5 px-4 py-1.5 bg-[#7B61FF] hover:bg-[#6B51EF] text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-40">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            Save
                        </button>
                    </div>
                </div>
            )}
        </Section>
    );
};

// ─── Hardware Section ─────────────────────────────────────────────────────────

const HardwareSection = ({ client, password, onRefresh }) => {
    const hw = client.hardware;
    const [available, setAvailable] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [assigning, setAssigning] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [allHw, setAllHw] = useState([]);
    const [newHw, setNewHw] = useState({ serial_number: '', model: '', label: '', notes: '' });
    const [addingHw, setAddingHw] = useState(false);
    const [loadingModal, setLoadingModal] = useState(false);

    const fetchAvailable = useCallback(async () => {
        try {
            const res = await fetch(`/api/manage-hardware?action=list&password=${encodeURIComponent(password)}`);
            if (!res.ok) return;
            const data = await res.json();
            const list = data.hardware || data || [];
            setAvailable(list.filter(h => h.status === 'available'));
        } catch { /* silent */ }
    }, [password]);

    useEffect(() => {
        if (!hw) fetchAvailable();
    }, [hw, fetchAvailable]);

    const fetchAll = async () => {
        setLoadingModal(true);
        try {
            const res = await fetch(`/api/manage-hardware?action=list&password=${encodeURIComponent(password)}`);
            if (!res.ok) return;
            const data = await res.json();
            setAllHw(data.hardware || data || []);
        } catch { /* silent */ }
        finally { setLoadingModal(false); }
    };

    const openModal = () => { setShowModal(true); fetchAll(); };

    const handleAssign = async () => {
        if (!selectedId) return;
        setAssigning(true);
        try {
            const res = await fetch('/api/update-build-package', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ client_id: client.id, updates: { hardware_id: selectedId } }),
            });
            if (!res.ok) throw new Error('Failed to assign hardware');
            await onRefresh();
        } catch (err) { alert(err.message); }
        finally { setAssigning(false); }
    };

    const handleUnassign = async () => {
        if (!window.confirm('Unassign this hardware?')) return;
        setAssigning(true);
        try {
            const res = await fetch('/api/update-build-package', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ client_id: client.id, updates: { hardware_id: null } }),
            });
            if (!res.ok) throw new Error('Failed to unassign');
            await onRefresh();
        } catch (err) { alert(err.message); }
        finally { setAssigning(false); }
    };

    const handleAddHw = async () => {
        if (!newHw.serial_number || !newHw.model) return alert('Serial number and model required');
        setAddingHw(true);
        try {
            const res = await fetch('/api/manage-hardware', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ action: 'add', ...newHw }),
            });
            if (!res.ok) throw new Error('Failed to add hardware');
            setNewHw({ serial_number: '', model: '', label: '', notes: '' });
            await fetchAll();
        } catch (err) { alert(err.message); }
        finally { setAddingHw(false); }
    };

    const handleDeleteHw = async (id) => {
        if (!window.confirm('Delete this hardware entry?')) return;
        try {
            const res = await fetch('/api/manage-hardware', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ action: 'delete', id }),
            });
            if (!res.ok) throw new Error('Failed to delete');
            await fetchAll();
        } catch (err) { alert(err.message); }
    };

    const inputCls = 'bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-lg px-3 py-2 text-sm text-[#F0EFF4] placeholder:text-[#F0EFF4]/20 focus:border-[#7B61FF] focus:outline-none transition-colors w-full';

    return (
        <>
            <Section
                title="Hardware Assignment"
                icon={HardDrive}
                action={
                    <button
                        onClick={openModal}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F0EFF4]/5 hover:bg-[#F0EFF4]/10 border border-[#F0EFF4]/10 rounded-lg text-xs text-[#F0EFF4]/60 hover:text-[#F0EFF4] font-semibold transition-colors"
                    >
                        <Monitor className="w-3.5 h-3.5" /> Manage Hardware
                    </button>
                }
            >
                {hw ? (
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <HardDrive className="w-8 h-8 text-[#7B61FF] shrink-0" />
                            <div>
                                <div className="font-semibold text-[#F0EFF4]">{hw.label || hw.model}</div>
                                <div className="text-xs text-[#F0EFF4]/50 font-data">{hw.model} · SN: {hw.serial_number}</div>
                                {hw.notes && <div className="text-xs text-[#F0EFF4]/40 mt-0.5">{hw.notes}</div>}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-data font-semibold ${HW_STATUS_STYLES[hw.status] || 'bg-[#F0EFF4]/10 text-[#F0EFF4]/50'}`}>
                                {hw.status}
                            </span>
                            <button
                                onClick={handleUnassign}
                                disabled={assigning}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg text-xs text-red-400 font-semibold transition-colors"
                            >
                                {assigning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                                Unassign
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm text-[#F0EFF4]/40">No hardware assigned.</p>
                        <div className="flex gap-3 items-center">
                            <select
                                value={selectedId}
                                onChange={e => setSelectedId(e.target.value)}
                                className="flex-1 bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-lg px-3 py-2 text-sm text-[#F0EFF4] focus:border-[#7B61FF] focus:outline-none appearance-none"
                            >
                                <option value="">Select available hardware…</option>
                                {available.map(h => (
                                    <option key={h.id} value={h.id}>{h.label || h.model} — {h.serial_number}</option>
                                ))}
                            </select>
                            <button
                                onClick={handleAssign}
                                disabled={!selectedId || assigning}
                                className="flex items-center gap-1.5 px-4 py-2 bg-[#7B61FF] hover:bg-[#6B51EF] text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-40 shrink-0"
                            >
                                {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                Assign
                            </button>
                        </div>
                    </div>
                )}
            </Section>

            {/* Hardware Management Modal */}
            {showModal && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-6" onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
                    <div className="relative w-full max-w-2xl bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-2xl overflow-hidden max-h-[80vh] flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F0EFF4]/10">
                            <h3 className="font-bold text-[#F0EFF4] flex items-center gap-2">
                                <HardDrive className="w-4 h-4 text-[#7B61FF]" /> Hardware Inventory
                            </h3>
                            <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-[#F0EFF4]/10 text-[#F0EFF4]/60"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {loadingModal ? (
                                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#7B61FF]" /></div>
                            ) : (
                                <div className="space-y-2">
                                    {allHw.length === 0 ? (
                                        <p className="text-sm text-[#F0EFF4]/30 italic py-4">No hardware in inventory.</p>
                                    ) : allHw.map(h => (
                                        <div key={h.id} className="flex items-center justify-between p-3 bg-[#18181B] rounded-xl border border-[#F0EFF4]/10">
                                            <div>
                                                <div className="font-semibold text-sm text-[#F0EFF4]">{h.label || h.model}</div>
                                                <div className="text-xs text-[#F0EFF4]/50 font-data">{h.model} · {h.serial_number}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-data font-semibold ${HW_STATUS_STYLES[h.status] || 'bg-[#F0EFF4]/10 text-[#F0EFF4]/50'}`}>
                                                    {h.status}
                                                </span>
                                                <button onClick={() => handleDeleteHw(h.id)} className="p-1 text-[#F0EFF4]/30 hover:text-red-400 transition-colors">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Add hardware form */}
                            <div className="border-t border-[#F0EFF4]/10 pt-5">
                                <h4 className="text-xs font-bold text-[#F0EFF4]/60 uppercase tracking-wider font-data mb-3">Add Hardware</h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <input className={inputCls} placeholder="Serial Number *" value={newHw.serial_number} onChange={e => setNewHw(h => ({ ...h, serial_number: e.target.value }))} />
                                    <input className={inputCls} placeholder="Model *" value={newHw.model} onChange={e => setNewHw(h => ({ ...h, model: e.target.value }))} />
                                    <input className={inputCls} placeholder="Label (e.g. M3 MacBook Air)" value={newHw.label} onChange={e => setNewHw(h => ({ ...h, label: e.target.value }))} />
                                    <input className={inputCls} placeholder="Notes" value={newHw.notes} onChange={e => setNewHw(h => ({ ...h, notes: e.target.value }))} />
                                </div>
                                <div className="flex justify-end mt-3">
                                    <button
                                        onClick={handleAddHw}
                                        disabled={addingHw}
                                        className="flex items-center gap-2 px-4 py-2 bg-[#7B61FF] hover:bg-[#6B51EF] text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-40"
                                    >
                                        {addingHw ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                        Add Hardware
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// ─── Sub-Agents Section ───────────────────────────────────────────────────────

const SubAgentsSection = ({ client, password, onRefresh }) => {
    const agents = client.build_package?.sub_agent_list || [];
    const [showAdd, setShowAdd] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ name: '', role: '', telegram_bot_token: '' });
    const [revealedTokens, setRevealedTokens] = useState({});
    const [cycling, setCycling] = useState(null);

    const STATUS_CYCLE = ['planned', 'configured', 'live'];

    const saveAgents = async (updatedList) => {
        const res = await fetch('/api/update-build-package', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
            body: JSON.stringify({ client_id: client.id, updates: { sub_agent_list: updatedList } }),
        });
        if (!res.ok) throw new Error('Failed to update sub-agents');
    };

    const handleAdd = async () => {
        if (!form.name) return alert('Name required');
        setSaving(true);
        try {
            const updated = [...agents, { ...form, status: 'planned', id: Date.now() }];
            await saveAgents(updated);
            setForm({ name: '', role: '', telegram_bot_token: '' });
            setShowAdd(false);
            await onRefresh();
        } catch (err) { alert(err.message); }
        finally { setSaving(false); }
    };

    const handleCycleStatus = async (idx) => {
        setCycling(idx);
        try {
            const current = agents[idx].status || 'planned';
            const nextIdx = (STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length;
            const updated = agents.map((a, i) => i === idx ? { ...a, status: STATUS_CYCLE[nextIdx] } : a);
            await saveAgents(updated);
            await onRefresh();
        } catch (err) { alert(err.message); }
        finally { setCycling(null); }
    };

    const handleDelete = async (idx) => {
        if (!window.confirm('Remove this sub-agent?')) return;
        try {
            const updated = agents.filter((_, i) => i !== idx);
            await saveAgents(updated);
            await onRefresh();
        } catch (err) { alert(err.message); }
    };

    const inputCls = 'bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-lg px-3 py-2 text-sm text-[#F0EFF4] placeholder:text-[#F0EFF4]/20 focus:border-[#7B61FF] focus:outline-none transition-colors w-full';

    return (
        <Section
            title="Sub-Agents"
            icon={Bot}
            action={
                <button
                    onClick={() => setShowAdd(v => !v)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7B61FF]/10 hover:bg-[#7B61FF]/20 border border-[#7B61FF]/20 rounded-lg text-xs text-[#7B61FF] font-semibold transition-colors"
                >
                    <Plus className="w-3.5 h-3.5" /> Add Sub-Agent
                </button>
            }
        >
            {agents.length === 0 && !showAdd ? (
                <p className="text-sm text-[#F0EFF4]/30 italic py-2">No sub-agents configured.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-[#F0EFF4]/10 text-[#F0EFF4]/40 font-data uppercase tracking-wider">
                                <th className="text-left pb-2 pr-3">Name</th>
                                <th className="text-left pb-2 pr-3">Role</th>
                                <th className="text-left pb-2 pr-3">Bot Token</th>
                                <th className="text-left pb-2 pr-3">Status</th>
                                <th className="text-right pb-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F0EFF4]/5">
                            {agents.map((agent, idx) => (
                                <tr key={agent.id || idx} className="group">
                                    <td className="py-2 pr-3 font-semibold text-[#F0EFF4]">{agent.name}</td>
                                    <td className="py-2 pr-3 text-[#F0EFF4]/60">{agent.role || '—'}</td>
                                    <td className="py-2 pr-3">
                                        <div className="flex items-center gap-1">
                                            <MaskedValue
                                                value={agent.telegram_bot_token}
                                                id={`token-${idx}`}
                                                revealed={revealedTokens[`token-${idx}`]}
                                                onToggle={k => setRevealedTokens(r => ({ ...r, [k]: !r[k] }))}
                                            />
                                        </div>
                                    </td>
                                    <td className="py-2 pr-3">
                                        <button
                                            onClick={() => handleCycleStatus(idx)}
                                            disabled={cycling === idx}
                                            className={`px-2.5 py-1 rounded-full text-xs font-data font-semibold transition-all hover:scale-105 ${AGENT_STATUS_STYLES[agent.status] || AGENT_STATUS_STYLES.planned}`}
                                            title="Click to cycle status"
                                        >
                                            {cycling === idx ? <Loader2 className="w-3 h-3 animate-spin inline" /> : (agent.status || 'planned')}
                                        </button>
                                    </td>
                                    <td className="py-2 text-right">
                                        <button
                                            onClick={() => handleDelete(idx)}
                                            className="p-1 text-[#F0EFF4]/30 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showAdd && (
                <div className="mt-4 p-4 bg-[#0A0A14]/60 rounded-xl border border-[#7B61FF]/20 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <input className={inputCls} placeholder="Agent Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                        <input className={inputCls} placeholder="Role (e.g. Scheduler)" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} />
                    </div>
                    <input className={inputCls} placeholder="Telegram Bot Token" value={form.telegram_bot_token} onChange={e => setForm(f => ({ ...f, telegram_bot_token: e.target.value }))} />
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs text-[#F0EFF4]/40 hover:text-[#F0EFF4]">Cancel</button>
                        <button onClick={handleAdd} disabled={saving} className="flex items-center gap-1.5 px-4 py-1.5 bg-[#7B61FF] hover:bg-[#6B51EF] text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-40">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            Save
                        </button>
                    </div>
                </div>
            )}
        </Section>
    );
};

// ─── Knowledge Base Section ───────────────────────────────────────────────────

const DocTypeIcon = ({ fileType }) => {
    if (fileType === 'pdf') return <FileText className="w-5 h-5 text-red-400 shrink-0" />;
    if (fileType === 'xlsx' || fileType === 'csv') return <Table className="w-5 h-5 text-emerald-400 shrink-0" />;
    if (fileType === 'link') return <Link2 className="w-5 h-5 text-[#7B61FF] shrink-0" />;
    if (fileType === 'note') return <StickyNote className="w-5 h-5 text-yellow-400 shrink-0" />;
    return <FileText className="w-5 h-5 text-blue-400 shrink-0" />;
};

const SOURCE_STYLES = {
    client: 'bg-blue-500/20 text-blue-400',
    adam:   'bg-[#7B61FF]/20 text-[#7B61FF]',
    vito:   'bg-emerald-500/20 text-emerald-400',
};

const KnowledgeBaseSection = ({ client, password, onRefresh }) => {
    const docs = client.documents || [];
    const [showAdd, setShowAdd] = useState(false);
    const [addType, setAddType] = useState('file');
    const [file, setFile] = useState(null);
    const [form, setForm] = useState({ description: '', source_context: '', source: 'client', url: '', note_content: '' });
    const [uploading, setUploading] = useState(false);
    const [markingReviewed, setMarkingReviewed] = useState(null);
    const [togglingInclude, setTogglingInclude] = useState(null);
    const fileInputRef = useRef(null);

    const handleUpload = async () => {
        if (addType === 'file' && !file) return alert('Select a file');
        if (addType === 'link' && !form.url) return alert('URL required');
        setUploading(true);
        try {
            if (addType === 'file') {
                const fd = new FormData();
                fd.append('file', file);
                fd.append('client_id', client.id);
                fd.append('description', form.description);
                fd.append('source_context', form.source_context);
                fd.append('source', form.source);
                const res = await fetch('/api/upload-client-document', {
                    method: 'POST',
                    headers: { 'x-admin-password': password },
                    body: fd,
                });
                if (!res.ok) throw new Error('Upload failed');
            } else {
                const res = await fetch('/api/upload-client-document', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                    body: JSON.stringify({
                        client_id: client.id,
                        file_type: addType,
                        description: form.description,
                        source_context: form.source_context,
                        source: form.source,
                        url: form.url,
                        note_content: form.note_content,
                    }),
                });
                if (!res.ok) throw new Error('Failed to add');
            }
            setForm({ description: '', source_context: '', source: 'client', url: '', note_content: '' });
            setFile(null);
            setShowAdd(false);
            await onRefresh();
        } catch (err) {
            alert(err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleMarkReviewed = async (docId) => {
        setMarkingReviewed(docId);
        try {
            const res = await fetch('/api/upload-client-document', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ id: docId, client_id: client.id, status: 'reviewed', reviewed_at: new Date().toISOString() }),
            });
            if (!res.ok) throw new Error('Failed to mark reviewed');
            await onRefresh();
        } catch (err) { alert(err.message); }
        finally { setMarkingReviewed(null); }
    };

    const handleToggleInclude = async (doc) => {
        setTogglingInclude(doc.id);
        try {
            const newInclude = !doc.include_in_build;
            const res = await fetch('/api/upload-client-document', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({
                    id: doc.id, client_id: client.id,
                    include_in_build: newInclude,
                    status: newInclude ? 'included_in_build' : 'reviewed',
                }),
            });
            if (!res.ok) throw new Error('Failed to update');
            await onRefresh();
        } catch (err) { alert(err.message); }
        finally { setTogglingInclude(null); }
    };

    const inputCls = 'bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-lg px-3 py-2 text-sm text-[#F0EFF4] placeholder:text-[#F0EFF4]/20 focus:border-[#7B61FF] focus:outline-none transition-colors w-full';

    return (
        <Section
            title="Knowledge Base"
            icon={BookOpen}
            action={
                <button
                    onClick={() => setShowAdd(v => !v)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7B61FF]/10 hover:bg-[#7B61FF]/20 border border-[#7B61FF]/20 rounded-lg text-xs text-[#7B61FF] font-semibold transition-colors"
                >
                    <Upload className="w-3.5 h-3.5" /> Add
                </button>
            }
        >
            {docs.length === 0 && !showAdd ? (
                <p className="text-sm text-[#F0EFF4]/30 italic py-2">
                    No documents yet. Forward client files to Vito for filing.
                </p>
            ) : (
                <div className="space-y-3">
                    {docs.map((doc) => (
                        <div key={doc.id} className="bg-[#0A0A14]/60 border border-[#F0EFF4]/10 rounded-xl p-4">
                            <div className="flex items-start gap-3">
                                <DocTypeIcon fileType={doc.file_type} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-sm text-[#F0EFF4] truncate">{doc.filename || doc.description || 'Untitled'}</span>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-data font-semibold ${DOC_STATUS_STYLES[doc.status] || 'bg-[#F0EFF4]/10 text-[#F0EFF4]/50'}`}>
                                            {doc.status?.replace('_', ' ') || 'received'}
                                        </span>
                                        {doc.source && (
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-data font-semibold ${SOURCE_STYLES[doc.source] || 'bg-[#F0EFF4]/10 text-[#F0EFF4]/50'}`}>
                                                {doc.source}
                                            </span>
                                        )}
                                    </div>
                                    {doc.description && doc.filename && (
                                        <p className="text-xs text-[#F0EFF4]/50 mt-0.5">{doc.description}</p>
                                    )}
                                    {doc.source_context && (
                                        <p className="text-xs text-[#F0EFF4]/30 italic mt-0.5">{doc.source_context}</p>
                                    )}
                                    <div className="text-xs text-[#F0EFF4]/30 mt-1">
                                        {new Date(doc.created_at).toLocaleDateString()}
                                        {doc.reviewed_at && ` · reviewed ${new Date(doc.reviewed_at).toLocaleDateString()}`}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {/* Include in build toggle */}
                                    <button
                                        onClick={() => handleToggleInclude(doc)}
                                        disabled={togglingInclude === doc.id}
                                        title={doc.include_in_build ? 'Remove from build' : 'Include in build'}
                                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-data font-semibold border transition-all ${
                                            doc.include_in_build
                                                ? 'bg-[#7B61FF]/20 border-[#7B61FF]/30 text-[#7B61FF]'
                                                : 'bg-[#F0EFF4]/5 border-[#F0EFF4]/10 text-[#F0EFF4]/40 hover:text-[#F0EFF4]'
                                        }`}
                                    >
                                        {togglingInclude === doc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                        {doc.include_in_build ? 'Included' : 'Include'}
                                    </button>

                                    {doc.status !== 'reviewed' && doc.status !== 'included_in_build' && (
                                        <button
                                            onClick={() => handleMarkReviewed(doc.id)}
                                            disabled={markingReviewed === doc.id}
                                            className="px-2.5 py-1 rounded-full text-xs font-data font-semibold bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                                        >
                                            {markingReviewed === doc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Review'}
                                        </button>
                                    )}

                                    {doc.file_url && (
                                        <a
                                            href={doc.file_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-1.5 text-[#F0EFF4]/40 hover:text-[#7B61FF] transition-colors"
                                            title="Download"
                                        >
                                            <Download className="w-4 h-4" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add form */}
            {showAdd && (
                <div className="mt-4 p-4 bg-[#0A0A14]/60 rounded-xl border border-[#7B61FF]/20 space-y-3">
                    <div className="flex gap-2">
                        {[['file', 'File'], ['link', 'Link'], ['note', 'Note']].map(([type, label]) => (
                            <button
                                key={type}
                                onClick={() => setAddType(type)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${addType === type ? 'bg-[#7B61FF] text-white' : 'bg-[#F0EFF4]/5 text-[#F0EFF4]/60 hover:text-[#F0EFF4]'}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {addType === 'file' && (
                        <div>
                            <input ref={fileInputRef} type="file" className="hidden" onChange={e => setFile(e.target.files[0])} />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="flex items-center gap-2 px-4 py-2 bg-[#F0EFF4]/5 hover:bg-[#F0EFF4]/10 border border-dashed border-[#F0EFF4]/20 rounded-lg text-sm text-[#F0EFF4]/60 hover:text-[#F0EFF4] transition-colors w-full justify-center"
                            >
                                <Upload className="w-4 h-4" />
                                {file ? file.name : 'Choose file…'}
                            </button>
                        </div>
                    )}

                    {addType === 'link' && (
                        <input className={inputCls} placeholder="URL *" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
                    )}

                    {addType === 'note' && (
                        <textarea
                            className={inputCls + ' resize-y min-h-[80px]'}
                            placeholder="Note content…"
                            value={form.note_content}
                            onChange={e => setForm(f => ({ ...f, note_content: e.target.value }))}
                        />
                    )}

                    <input className={inputCls} placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                    <input className={inputCls} placeholder="Source context (e.g. 'Sent via email 2/15')" value={form.source_context} onChange={e => setForm(f => ({ ...f, source_context: e.target.value }))} />

                    <div className="flex gap-2 items-center">
                        <span className="text-xs text-[#F0EFF4]/40">Source:</span>
                        {['client', 'adam', 'vito'].map(s => (
                            <button key={s} onClick={() => setForm(f => ({ ...f, source: s }))}
                                className={`px-2.5 py-1 rounded-full text-xs font-data font-semibold transition-colors ${form.source === s ? SOURCE_STYLES[s] : 'bg-[#F0EFF4]/5 text-[#F0EFF4]/40 hover:text-[#F0EFF4]'}`}
                            >{s}</button>
                        ))}
                    </div>

                    <div className="flex justify-end gap-2">
                        <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs text-[#F0EFF4]/40 hover:text-[#F0EFF4]">Cancel</button>
                        <button onClick={handleUpload} disabled={uploading} className="flex items-center gap-1.5 px-4 py-1.5 bg-[#7B61FF] hover:bg-[#6B51EF] text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-40">
                            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            {addType === 'file' ? 'Upload' : 'Add'}
                        </button>
                    </div>
                </div>
            )}
        </Section>
    );
};

// ─── Build Notes Section ──────────────────────────────────────────────────────

const BuildNotesSection = ({ client, password, onUpdate }) => {
    const [notes, setNotes] = useState(client.build_package?.build_notes || '');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        setNotes(client.build_package?.build_notes || '');
    }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (e) => {
        const val = e.target.value;
        setNotes(val);
        setSaved(false);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => doSave(val), 1500);
    };

    const doSave = async (val) => {
        setSaving(true);
        try {
            const res = await fetch('/api/update-build-package', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ client_id: client.id, updates: { build_notes: val } }),
            });
            if (!res.ok) throw new Error('Failed to save notes');
            setSaved(true);
            onUpdate('build_notes', val);
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Section title="Build Notes" icon={StickyNote}>
            <div className="space-y-2">
                <textarea
                    value={notes}
                    onChange={handleChange}
                    onBlur={() => { if (timerRef.current) { clearTimeout(timerRef.current); doSave(notes); } }}
                    placeholder="Add build notes visible to the build team..."
                    className="w-full bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-xl px-4 py-3 text-sm text-[#F0EFF4] placeholder:text-[#F0EFF4]/20 focus:border-[#7B61FF] focus:outline-none transition-colors resize-y min-h-[100px]"
                    rows={4}
                />
                <div className="flex items-center justify-between px-1">
                    <span className="text-xs text-[#F0EFF4]/30 font-data">{notes.length} chars</span>
                    <span className="text-xs font-data">
                        {saving ? (
                            <span className="text-[#F0EFF4]/40 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> saving…</span>
                        ) : saved ? (
                            <span className="text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> saved</span>
                        ) : null}
                    </span>
                </div>
            </div>
        </Section>
    );
};

// ─── Action Bar ───────────────────────────────────────────────────────────────

const ActionBar = ({ client, password, onRefresh }) => {
    const bp = client.build_package;
    const p  = client.progress || {};
    const allPrereqs = p.intake_complete && p.apple_id_ready && p.tailscale_ready &&
                       p.agent_email_ready && p.credentials_submitted;
    const configsGenerated = bp && (bp.config_generated_at || bp.soul_md);
    const [generating, setGenerating]   = useState(false);
    const [marking, setMarking]         = useState(false);
    const [deploying, setDeploying]     = useState(false);

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const res = await fetch('/api/generate-config-files', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ client_id: client.id }),
            });
            if (!res.ok) throw new Error('Failed to generate');
            await onRefresh();
        } catch (err) { alert(err.message); }
        finally { setGenerating(false); }
    };

    const handleMarkReady = async () => {
        if (!window.confirm('Mark this build package as READY TO BUILD?')) return;
        setMarking(true);
        try {
            const res = await fetch('/api/update-build-package', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ client_id: client.id, updates: { status: 'ready' } }),
            });
            if (!res.ok) throw new Error('Failed to mark ready');
            await onRefresh();
        } catch (err) { alert(err.message); }
        finally { setMarking(false); }
    };

    const handleMarkDeployed = async () => {
        if (!window.confirm('Mark this build as DEPLOYED? This cannot be undone easily.')) return;
        setDeploying(true);
        try {
            const res = await fetch('/api/update-build-package', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ client_id: client.id, updates: { status: 'deployed', deployed_at: new Date().toISOString() } }),
            });
            if (!res.ok) throw new Error('Failed to mark deployed');
            await onRefresh();
        } catch (err) { alert(err.message); }
        finally { setDeploying(false); }
    };

    const handleDownload = () => {
        if (bp?.status !== 'ready' && bp?.status !== 'deployed') {
            alert('Build package must be marked Ready or Deployed before downloading.');
            return;
        }
        window.open(`/api/download-build-package?client_id=${client.id}&password=${encodeURIComponent(password)}`, '_blank');
    };

    return (
        <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-5">
            <h3 className="text-xs font-bold text-[#F0EFF4]/40 uppercase tracking-wider font-data mb-4">Build Actions</h3>
            <div className="flex flex-wrap gap-3">

                {/* Generate Configs — only if not yet generated */}
                {!configsGenerated && (
                    <button
                        onClick={handleGenerate}
                        disabled={generating}
                        className="flex items-center gap-2 px-5 py-2.5 bg-[#7B61FF] hover:bg-[#6B51EF] text-white text-sm font-bold rounded-xl transition-colors disabled:opacity-40"
                    >
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        Generate Config Files
                    </button>
                )}

                {/* Mark Ready — only if draft and all prereqs done */}
                {allPrereqs && configsGenerated && bp?.status === 'draft' && (
                    <button
                        onClick={handleMarkReady}
                        disabled={marking}
                        className="flex items-center gap-2 px-5 py-2.5 bg-[#F0EFF4]/5 hover:bg-[#F0EFF4]/10 border border-[#F0EFF4]/20 text-[#F0EFF4] text-sm font-bold rounded-xl transition-colors disabled:opacity-40"
                    >
                        {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        Mark Ready to Build
                    </button>
                )}

                {/* Download */}
                <button
                    onClick={handleDownload}
                    disabled={bp?.status !== 'ready' && bp?.status !== 'deployed'}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#7B61FF] hover:bg-[#6B51EF] disabled:opacity-30 text-white text-sm font-bold rounded-xl transition-colors"
                    title={bp?.status !== 'ready' && bp?.status !== 'deployed' ? 'Mark as Ready first' : 'Download Build Package'}
                >
                    <Download className="w-4 h-4" />
                    Download Build Package
                </button>

                {/* Mark Deployed */}
                {bp?.status !== 'deployed' && (
                    <button
                        onClick={handleMarkDeployed}
                        disabled={deploying || bp?.status !== 'ready'}
                        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-sm font-bold rounded-xl transition-colors disabled:opacity-30"
                        title={bp?.status !== 'ready' ? 'Package must be marked Ready first' : 'Mark as Deployed'}
                    >
                        {deploying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                        Mark as Deployed
                    </button>
                )}
            </div>
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ClientDetail = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const auth = useAuth();
    const { authenticated, loading: authLoading, password, getAuthHeaders } = auth;
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [mdEditorFile, setMdEditorFile] = useState(null);

    const fetchClient = useCallback(async () => {
        if (!authenticated && !authLoading) { navigate('/admin'); return; }
        if (authLoading) return; // Wait for auth to resolve
        try {
            const headers = getAuthHeaders();
            // Use password in query param for legacy compat, plus auth headers
            const url = password
                ? `/api/get-admin-pipeline?password=${encodeURIComponent(password)}`
                : '/api/get-admin-pipeline';
            const res = await fetch(url, { headers });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to load');
            const found = (data.pipeline || []).find(c => String(c.id) === String(clientId));
            if (!found) throw new Error('Client not found');
            setClient(found);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [clientId, password, authenticated, authLoading, getAuthHeaders, navigate]);

    useEffect(() => { fetchClient(); }, [fetchClient]);

    const refreshBuildData = useCallback(async () => {
        if (!authenticated) return;
        try {
            const headers = getAuthHeaders();
            const url = password
                ? `/api/get-admin-pipeline?password=${encodeURIComponent(password)}`
                : '/api/get-admin-pipeline';
            const res = await fetch(url, { headers });
            const data = await res.json();
            if (!res.ok) return;
            const found = (data.pipeline || []).find(c => String(c.id) === String(clientId));
            if (found) setClient(found);
        } catch { /* silent refresh failure */ }
    }, [clientId, password, authenticated, getAuthHeaders]);

    const handleIntakeUpdate = (fieldKey, newValue) => {
        setClient(prev => ({ ...prev, intake: { ...prev.intake, [fieldKey]: newValue } }));
    };

    const handleBuildPackageUpdate = (fieldKey, newValue) => {
        setClient(prev => ({
            ...prev,
            build_package: { ...(prev.build_package || {}), [fieldKey]: newValue }
        }));
    };

    const handleStageChange = async (targetStage) => {
        try {
            const res = await fetch('/api/update-client-status', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ client_id: client.id, stage: targetStage }),
            });
            if (!res.ok) throw new Error('Failed to update stage');
            setClient(prev => ({ ...prev, stage: targetStage }));
        } catch (err) { alert(err.message); }
    };

    const handleToggleStatus = async (field, currentValue) => {
        if (!window.confirm(`Mark ${field} as ${!currentValue ? 'COMPLETE' : 'INCOMPLETE'}?`)) return;
        try {
            const res = await fetch('/api/update-client-status', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({ client_id: client.id, [field]: !currentValue }),
            });
            if (!res.ok) throw new Error('Failed to update');
            setClient(prev => ({ ...prev, progress: { ...prev.progress, [field]: !currentValue } }));
        } catch (err) { alert(err.message); }
    };

    const handlePostUpdate = async (e) => {
        e.preventDefault();
        const fd = new FormData(e.target);
        const submitBtn = e.target.querySelector('button[type="submit"]');
        const ogText = submitBtn.textContent;
        submitBtn.disabled = true;
        submitBtn.textContent = 'Posting…';
        try {
            const res = await fetch('/api/post-client-update', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                body: JSON.stringify({
                    client_id: client.id,
                    message: fd.get('message'),
                    link_url: fd.get('link_url'),
                    link_label: fd.get('link_label'),
                    type: fd.get('type') || 'info',
                }),
            });
            if (!res.ok) throw new Error('Failed to post update');
            const { update } = await res.json();
            setClient(prev => ({ ...prev, client_updates: [update, ...(prev.client_updates || [])] }));
            e.target.reset();
        } catch (err) {
            alert(err.message);
        } finally {
            submitBtn.disabled = false;
            submitBtn.textContent = ogText;
        }
    };

    // ── Markdown editor file save callback ────────────────────────────────────
    const handleMdSaved = (fieldName, content) => {
        setClient(prev => ({
            ...prev,
            build_package: { ...(prev.build_package || {}), [fieldName]: content }
        }));
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0A0A14] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#7B61FF]" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#0A0A14] flex flex-col items-center justify-center text-[#F0EFF4] gap-4">
                <p className="text-red-400">{error}</p>
                <button onClick={() => navigate('/admin')} className="text-[#7B61FF] hover:underline text-sm">← Back to Dashboard</button>
            </div>
        );
    }

    const p = client.progress || {};
    const intake = client.intake || {};
    const portalUrl = client.portal_url || `https://www.roofclaw.com/onboard/${client.token}`;
    const stageColorClass = STAGE_COLORS[client.stage] || 'bg-white/5 text-white/40 ring-white/10';
    const days = daysAgo(client.created_at);

    const prereqs = [
        { key: 'intake_complete',       label: 'Intake' },
        { key: 'apple_id_ready',        label: 'Apple ID' },
        { key: 'agent_email_ready',     label: 'Agent Email' },
        { key: 'credentials_submitted', label: 'Credentials' },
        { key: 'tailscale_ready',       label: 'Tailscale' },
    ];

    return (
        <div className="min-h-screen bg-[#0A0A14] text-[#F0EFF4] font-heading">
            {/* Header */}
            <header className="border-b border-[#F0EFF4]/10 bg-[#0A0A14] sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/admin')}
                            className="flex items-center gap-1.5 text-sm text-[#F0EFF4]/60 hover:text-[#F0EFF4] transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Dashboard
                        </button>
                        <div className="w-px h-6 bg-[#F0EFF4]/10" />
                        <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-[#7B61FF]" />
                            <span className="font-bold">
                                Roof<span className="text-[#7B61FF]">Claw</span>
                                <span className="text-[#F0EFF4]/40 text-xs font-data ml-2">CLIENT</span>
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigator.clipboard.writeText(portalUrl)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F0EFF4]/5 hover:bg-[#F0EFF4]/10 border border-[#F0EFF4]/10 rounded-lg text-xs text-[#F0EFF4]/70 hover:text-[#F0EFF4] transition-all font-semibold"
                        >
                            <Copy className="w-3.5 h-3.5" />
                            Copy Portal URL
                        </button>
                        <a
                            href={portalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7B61FF]/10 hover:bg-[#7B61FF]/20 border border-[#7B61FF]/30 rounded-lg text-xs text-[#7B61FF] transition-all font-semibold"
                        >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Open Portal
                        </a>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">

                {/* Client Header Card */}
                <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                        <div className="space-y-3">
                            <h1 className="text-3xl font-bold">{client.name || '—'}</h1>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-[#F0EFF4]/60">
                                {client.email && (
                                    <a href={`mailto:${client.email}`} className="flex items-center gap-1.5 hover:text-[#7B61FF] transition-colors">
                                        <Mail className="w-3.5 h-3.5" /> {client.email}
                                    </a>
                                )}
                                {client.phone && (
                                    <a href={`tel:${client.phone}`} className="flex items-center gap-1.5 hover:text-[#7B61FF] transition-colors">
                                        <Phone className="w-3.5 h-3.5" /> {client.phone}
                                    </a>
                                )}
                                {client.company && (
                                    <span className="flex items-center gap-1.5">
                                        <Building2 className="w-3.5 h-3.5" /> {client.company}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-[#F0EFF4]/40">
                                <span>Joined {days}d ago</span>
                                {client.created_at && <span>({new Date(client.created_at).toLocaleDateString()})</span>}
                                {client.stripe_customer_id && (
                                    <span className="text-[#F0EFF4]/30">Stripe: {client.stripe_customer_id}</span>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-3 shrink-0">
                            <StageDropdown
                                stage={client.stage || 'Committed'}
                                onSelect={handleStageChange}
                                colorClass={stageColorClass}
                            />
                        </div>
                    </div>
                </div>

                {/* Build Status Banner */}
                <BuildStatusBanner client={client} />

                {/* Email Segment + CRM Status */}
                <EmailSegmentBadge
                    segment={client.email_segment}
                    suppressUntil={client.suppress_email_until}
                    lastVitoContact={client.last_vito_contact}
                    lastAutoEmail={client.last_automated_email_sent}
                    clientId={client.id}
                    password={password}
                    onRefresh={refreshBuildData}
                />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column — Details */}
                    <div className="lg:col-span-2 space-y-6">

                        {/* Prerequisites */}
                        <Section title="Prerequisites" icon={CheckCircle2}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {prereqs.map(({ key, label }) => (
                                    <button
                                        key={key}
                                        onClick={() => handleToggleStatus(key, p[key])}
                                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer
                                        ${p[key]
                                            ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20'
                                            : 'bg-[#0A0A14]/50 border-[#F0EFF4]/10 hover:bg-[#F0EFF4]/5'
                                        }`}
                                    >
                                        {p[key]
                                            ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                                            : <Circle className="w-5 h-5 text-[#F0EFF4]/20 shrink-0" />
                                        }
                                        <span className={`text-sm font-medium ${p[key] ? 'text-emerald-400' : 'text-[#F0EFF4]/60'}`}>
                                            {label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </Section>

                        {/* Agent Profile */}
                        <Section title="Agent Profile" icon={Zap}>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {[
                                    { label: 'Agent Name',     key: 'agent_name' },
                                    { label: 'Agent Role',     key: 'agent_role' },
                                    { label: 'Comm Style',     key: 'comm_style' },
                                    { label: 'Pushback Level', key: 'pushback_level' },
                                    { label: 'Channel',        key: 'preferred_channel' },
                                    { label: 'Active Hours',   key: 'active_hours' },
                                    { label: 'Agent Email',    key: 'agent_email' },
                                    { label: 'Apple ID Email', key: 'apple_id_email' },
                                    { label: 'Morning Brief',  key: 'morning_briefing' },
                                    { label: 'Proactive',      key: 'proactive_checkins' },
                                ].map(({ label, key }) => (
                                    <EditableField
                                        key={key}
                                        label={label}
                                        value={intake[key]}
                                        fieldKey={key}
                                        clientId={client.id}
                                        password={password}
                                        getAuthHeaders={getAuthHeaders}
                                        onUpdate={handleIntakeUpdate}
                                    />
                                ))}
                            </div>
                        </Section>

                        {/* Goals & Success Criteria */}
                        <Section title="Goals & Success Criteria" icon={Zap}>
                            <div className="space-y-5 text-sm">
                                <div className="grid grid-cols-1 gap-4">
                                    <EditableField label="Top Problem to Solve" value={intake.top_problem} fieldKey="top_problem" clientId={client.id} password={password} getAuthHeaders={getAuthHeaders} onUpdate={handleIntakeUpdate} />
                                    <EditableField label="Jobs Agent Could Take Over" value={intake.could_replace} fieldKey="could_replace" clientId={client.id} password={password} getAuthHeaders={getAuthHeaders} onUpdate={handleIntakeUpdate} />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <EditableField label="🎯 30-Day Success" value={intake.success_30_days} fieldKey="success_30_days" clientId={client.id} password={password} getAuthHeaders={getAuthHeaders} onUpdate={handleIntakeUpdate} />
                                    <EditableField label="🏆 90-Day Success" value={intake.success_90_days} fieldKey="success_90_days" clientId={client.id} password={password} getAuthHeaders={getAuthHeaders} onUpdate={handleIntakeUpdate} />
                                </div>
                                <div className="grid grid-cols-1 gap-4">
                                    <EditableField label="Priority 1" value={intake.priority_1} fieldKey="priority_1" clientId={client.id} password={password} getAuthHeaders={getAuthHeaders} onUpdate={handleIntakeUpdate} />
                                    <EditableField label="Priority 2" value={intake.priority_2} fieldKey="priority_2" clientId={client.id} password={password} getAuthHeaders={getAuthHeaders} onUpdate={handleIntakeUpdate} />
                                    <EditableField label="Priority 3" value={intake.priority_3} fieldKey="priority_3" clientId={client.id} password={password} getAuthHeaders={getAuthHeaders} onUpdate={handleIntakeUpdate} />
                                </div>
                            </div>
                        </Section>

                        {/* Business Info */}
                        <Section title="Business Info" icon={Building2}>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {[
                                    { label: 'Owner',          key: 'owner_name' },
                                    { label: 'Owner Role',     key: 'owner_role' },
                                    { label: 'Company',        key: 'company_name' },
                                    { label: 'Website',        key: 'company_website' },
                                    { label: 'Location',       key: 'location' },
                                    { label: 'Team Size',      key: 'team_size' },
                                    { label: 'Specialization', key: 'specialization' },
                                ].map(({ label, key }) => (
                                    <EditableField
                                        key={key}
                                        label={label}
                                        value={intake[key]}
                                        fieldKey={key}
                                        clientId={client.id}
                                        password={password}
                                        getAuthHeaders={getAuthHeaders}
                                        onUpdate={handleIntakeUpdate}
                                    />
                                ))}
                            </div>
                        </Section>

                        {/* Additional Details */}
                        <Section title="Additional Details" icon={MessageSquare}>
                            <div className="space-y-4 text-sm">
                                <EditableField label="Values & Guardrails" value={intake.values_guardrails} fieldKey="values_guardrails" clientId={client.id} password={password} getAuthHeaders={getAuthHeaders} onUpdate={handleIntakeUpdate} />
                                <EditableField label="Additional Notes" value={intake.additional_notes} fieldKey="additional_notes" clientId={client.id} password={password} getAuthHeaders={getAuthHeaders} onUpdate={handleIntakeUpdate} />
                                {p.intake_submitted_at && (
                                    <div className="text-xs text-[#F0EFF4]/30 pt-2 border-t border-[#F0EFF4]/5">
                                        Submitted {new Date(p.intake_submitted_at).toLocaleString()}
                                        {intake.last_edited_at && (
                                            <span className="ml-2">· Last edited {new Date(intake.last_edited_at).toLocaleString()} by {intake.last_edited_by || 'admin'}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Section>

                        {/* Tech Stack */}
                        <Section title="Tech Stack" icon={Building2}>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {[
                                    { label: 'CRM',          key: 'crm_tools' },
                                    { label: 'Project Mgmt', key: 'pm_tools' },
                                    { label: 'Phone',        key: 'phone_tools' },
                                    { label: 'Meetings',     key: 'meeting_tools' },
                                    { label: 'Comms',        key: 'comms_tools' },
                                    { label: 'Scheduling',   key: 'scheduling_tools' },
                                    { label: 'Accounting',   key: 'accounting_tools' },
                                    { label: 'Field/Ops',    key: 'field_tools' },
                                    { label: 'Marketing',    key: 'marketing_tools' },
                                    { label: 'Other',        key: 'other_tools' },
                                ].map(({ label, key }) => (
                                    <EditableField
                                        key={key}
                                        label={label}
                                        value={intake[key]}
                                        fieldKey={key}
                                        clientId={client.id}
                                        password={password}
                                        getAuthHeaders={getAuthHeaders}
                                        onUpdate={handleIntakeUpdate}
                                    />
                                ))}
                            </div>
                        </Section>

                        {/* ── Dossier Sections ─────────────────────────────── */}

                        <ConfigFilesSection
                            client={client}
                            password={password}
                            onRefresh={refreshBuildData}
                            onEditFile={setMdEditorFile}
                        />

                        <CredentialsSection
                            client={client}
                            password={password}
                            onRefresh={refreshBuildData}
                        />

                        <HardwareSection
                            client={client}
                            password={password}
                            onRefresh={refreshBuildData}
                        />

                        <SubAgentsSection
                            client={client}
                            password={password}
                            onRefresh={refreshBuildData}
                        />

                        <KnowledgeBaseSection
                            client={client}
                            password={password}
                            onRefresh={refreshBuildData}
                        />

                        <BuildNotesSection
                            client={client}
                            password={password}
                            onUpdate={handleBuildPackageUpdate}
                        />

                        {/* Client Notes & Activity */}
                        <ClientNotes
                            clientId={client.id}
                            password={password}
                            getAuthHeaders={getAuthHeaders}
                        />

                    </div>

                    {/* Right Column — Activity Feed */}
                    <div className="space-y-6">

                        {/* Post Update */}
                        <Section title="Post Update" icon={Send}>
                            <form onSubmit={handlePostUpdate} className="space-y-3">
                                <input
                                    required
                                    name="message"
                                    type="text"
                                    placeholder="Update message…"
                                    className="w-full bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-lg px-3 py-2 text-sm text-[#F0EFF4] placeholder:text-[#F0EFF4]/30 focus:border-[#7B61FF] focus:outline-none transition-colors"
                                />
                                <div className="flex gap-2">
                                    <input
                                        name="link_url"
                                        type="url"
                                        placeholder="URL (optional)"
                                        className="flex-1 bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-lg px-3 py-2 text-sm text-[#F0EFF4] placeholder:text-[#F0EFF4]/30 focus:border-[#7B61FF] focus:outline-none transition-colors"
                                    />
                                    <input
                                        name="link_label"
                                        type="text"
                                        placeholder="Label"
                                        className="w-20 bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-lg px-3 py-2 text-sm text-[#F0EFF4] placeholder:text-[#F0EFF4]/30 focus:border-[#7B61FF] focus:outline-none transition-colors"
                                    />
                                </div>
                                <select
                                    name="type"
                                    className="w-full bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-lg px-3 py-2 text-sm text-[#F0EFF4] focus:border-[#7B61FF] focus:outline-none appearance-none"
                                >
                                    <option value="info">ℹ️ Info</option>
                                    <option value="milestone">🏆 Milestone</option>
                                    <option value="link">👀 Link Drop</option>
                                    <option value="shipped">📦 Shipped</option>
                                </select>
                                <button
                                    type="submit"
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#7B61FF] hover:bg-[#6b52e8] text-white text-sm font-bold rounded-lg transition-colors"
                                >
                                    <Send className="w-3.5 h-3.5" /> Post Update
                                </button>
                            </form>
                        </Section>

                        {/* Full Activity Feed */}
                        <Section title="Activity Feed" icon={Clock}>
                            {(client.client_updates || []).length > 0 ? (
                                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                                    {(client.client_updates || []).map((update, i) => (
                                        <div key={i} className="flex gap-3 text-sm py-2.5 px-3 bg-[#0A0A14]/50 rounded-lg border border-[#F0EFF4]/5">
                                            <span className="shrink-0 text-base">{UPDATE_ICONS[update.type] || 'ℹ️'}</span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[#F0EFF4]/80">{update.message}</p>
                                                {update.link_url && (
                                                    <a href={update.link_url} className="text-[#7B61FF] font-medium hover:underline text-xs mt-1 block" target="_blank" rel="noreferrer">
                                                        {update.link_label || update.link_url}
                                                    </a>
                                                )}
                                                <span className="text-xs text-[#F0EFF4]/30 mt-1 block">
                                                    {new Date(update.created_at).toLocaleString()}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-[#F0EFF4]/30 italic text-center py-4">No activity yet</p>
                            )}
                        </Section>
                    </div>
                </div>

                {/* Action Bar */}
                <ActionBar
                    client={client}
                    password={password}
                    onRefresh={refreshBuildData}
                />

            </main>

            {/* Markdown Editor Modal */}
            {mdEditorFile && (
                <ModalErrorBoundary onClose={() => setMdEditorFile(null)}>
                    <Suspense fallback={
                        <div className="fixed inset-0 z-[200] bg-[#0A0A14] flex items-center justify-center">
                            <Loader2 className="w-8 h-8 animate-spin text-[#7B61FF]" />
                        </div>
                    }>
                        <MarkdownEditorModal
                            file={mdEditorFile}
                            clientId={client.id}
                            password={password}
                            onClose={() => setMdEditorFile(null)}
                            onSaved={handleMdSaved}
                        />
                    </Suspense>
                </ModalErrorBoundary>
            )}
        </div>
    );
};

export default ClientDetail;
